import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    // Return the data directly if response has success property
    if (response.data && response.data.hasOwnProperty("success")) {
      if (response.data.success === false) {
        return Promise.reject(response.data);
      }
      return response.data;
    }
    return response.data;
  },
  (error) => {
    console.error("Response interceptor error:", error);

    if (error.response?.status === 401) {
      localStorage.removeItem("adminToken");
      window.location.href = "/login";
    }

    const message =
      error.response?.data?.message || error.message || "An error occurred";
    return Promise.reject({ message, ...error.response?.data });
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  getMe: () => api.get("/auth/me"),
  createAdmin: (data) => api.post("/auth/create-admin", data),
  changePassword: (data) => api.post("/auth/change-password", data),
};

// Projects API
export const projectsAPI = {
  getProjects: (params = {}) => api.get("/projects", { params }),
  getProject: (id) => api.get(`/projects/${id}`),
  createProject: (formData) => {
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000, // 60 seconds for file uploads
    };
    return api.post("/projects", formData, config);
  },
  updateProject: (id, formData) => {
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    };
    return api.put(`/projects/${id}`, formData, config);
  },
  deleteProject: (id) => api.delete(`/projects/${id}`),
  reorderProjects: (projects) => api.patch("/projects/reorder", { projects }),
};

// Skills API
export const skillsAPI = {
  getSkills: () => api.get("/skills"),
  createSkill: (data) => api.post("/skills", data),
  updateSkill: (id, data) => api.put(`/skills/${id}`, data),
  deleteSkill: (id) => api.delete(`/skills/${id}`),
};

// Profile API
export const profileAPI = {
  getProfile: () => api.get("/profile"),
  updateProfile: (formData) => {
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    };
    return api.put("/profile", formData, config);
  },
};

// Contacts API
export const contactsAPI = {
  getContacts: () => api.get("/contact"),
  updateContactStatus: (id, status) => api.patch(`/contact/${id}`, { status }),
  deleteContact: (id) => api.delete(`/contact/${id}`),
};

// Health check
export const healthCheck = () => api.get("/health");

// Helper function to get image URL
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // If it's already a full URL (Cloudinary), return as-is
  if (imagePath.startsWith("http")) {
    return imagePath;
  }

  // If it's a local path, prepend the API base URL
  const baseUrl =
    process.env.REACT_APP_API_URL?.replace("/api", "") ||
    "http://localhost:5000";
  return `${baseUrl}${imagePath}`;
};

export default api;
